import { useMemo, useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { MaskLine } from "../components/Reveal";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useLanguage } from "../i18n/LanguageContext";

const SQFT_PER_SQM = 10.7639;

const Field = ({ label, children, hint }) => (
  <div>
    <label className="label mb-3 block text-muted-foreground">{label}</label>
    {children}
    {hint && <p className="mt-2 text-xs text-black/40">{hint}</p>}
  </div>
);

const UnitToggle = ({ unit, setUnit, testid }) => {
  const { t } = useLanguage();
  return (
    <div className="inline-flex border border-black/15" data-testid={testid}>
      {["sqft", "sqm"].map((u) => (
        <button
          key={u}
          onClick={() => setUnit(u)}
          className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
            unit === u ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {u === "sqft" ? t("calculators.sqft") : t("calculators.sqm")}
        </button>
      ))}
    </div>
  );
};

const ResultCard = ({ value, unit, label, big }) => (
  <div className={`min-w-0 overflow-hidden border border-black/10 bg-secondary/40 p-4 sm:p-6 ${big ? "sm:col-span-2" : ""}`}>
    <div className="label break-words text-muted-foreground">{label}</div>
    <div className="mt-2 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className={`font-mono font-bold text-primary ${big ? "text-3xl sm:text-5xl md:text-6xl" : "text-xl sm:text-3xl"}`}>{value}</span>
      <span className="text-sm text-muted-foreground">{unit}</span>
    </div>
  </div>
);

function AdhesiveCalc() {
  const { t } = useLanguage();
  const [area, setArea] = useState("100");
  const [unit, setUnit] = useState("sqft");
  const [trowel, setTrowel] = useState("3");
  const [bag, setBag] = useState("20");
  const [waste, setWaste] = useState(true);

  const result = useMemo(() => {
    const a = parseFloat(area) || 0;
    const areaSqm = unit === "sqft" ? a / SQFT_PER_SQM : a;
    const bed = parseFloat(trowel); // mm bed thickness
    const consumption = bed * 1.3; // kg/m² (matches 3mm ≈ 3.9 kg/m²)
    let totalKg = areaSqm * consumption;
    if (waste) totalKg *= 1.05;
    const bagKg = parseFloat(bag);
    const bags = totalKg > 0 ? Math.ceil(totalKg / bagKg) : 0;
    return {
      areaSqm: areaSqm.toFixed(1),
      consumption: consumption.toFixed(1),
      totalKg: totalKg.toFixed(1),
      bags,
    };
  }, [area, unit, trowel, bag, waste]);

  return (
    <div className="grid w-full min-w-0 max-w-full overflow-hidden gap-px bg-black/10 lg:grid-cols-2" data-testid="adhesive-calculator">
      {/* Inputs */}
      <div className="space-y-8 bg-background p-8 md:p-12">
        <Field label={t("calculators.totalArea")}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              type="number"
              min="0"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              data-testid="adhesive-area-input"
              className="h-12 rounded-none border-black/15 bg-secondary/40 font-mono text-lg"
            />
            <UnitToggle unit={unit} setUnit={setUnit} testid="adhesive-unit-toggle" />
          </div>
        </Field>

        <Field label={t("calculators.bedThickness")} hint={t("calculators.bedHint")}>
          <Select value={trowel} onValueChange={setTrowel}>
            <SelectTrigger data-testid="adhesive-trowel-select" className="h-12 rounded-none border-black/15 bg-secondary/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="3">{t("calculators.bed3")}</SelectItem>
              <SelectItem value="4">{t("calculators.bed4")}</SelectItem>
              <SelectItem value="5">{t("calculators.bed5")}</SelectItem>
              <SelectItem value="6">{t("calculators.bed6")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("calculators.bagSize")}>
          <Select value={bag} onValueChange={setBag}>
            <SelectTrigger data-testid="adhesive-bag-select" className="h-12 rounded-none border-black/15 bg-secondary/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="20">{t("calculators.bag20")}</SelectItem>
              <SelectItem value="40">{t("calculators.bag40")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <button
          onClick={() => setWaste((w) => !w)}
          data-testid="adhesive-waste-toggle"
          className="flex items-center gap-3 text-left"
        >
          <span className={`flex h-5 w-5 items-center justify-center border ${waste ? "border-primary bg-primary" : "border-black/30"}`}>
            {waste && <span className="text-[10px] text-primary-foreground">✓</span>}
          </span>
          <span className="text-sm text-muted-foreground">{t("calculators.waste")}</span>
        </button>
      </div>

      {/* Results */}
      <div className="min-w-0 max-w-full overflow-hidden bg-secondary/20 p-4 sm:p-8 md:p-12">
        <span className="label text-primary">{t("calculators.estimated")}</span>
        <AnimatePresence mode="wait">
          <motion.div
            key={result.bags + result.totalKg}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 grid w-full min-w-0 grid-cols-1 gap-px bg-black/10 sm:grid-cols-2"
          >
            <ResultCard big value={result.bags} unit={t("calculators.bags")} label={t("calculators.bagsRequired")} />
            <ResultCard value={result.totalKg} unit="kg" label={t("calculators.totalAdhesive")} />
            <ResultCard value={result.consumption} unit="kg/m²" label={t("calculators.consumption")} />
            <ResultCard value={result.areaSqm} unit="m²" label={t("calculators.area")} />
          </motion.div>
        </AnimatePresence>
        <p className="mt-6 text-xs leading-relaxed text-black/40">
          {t("calculators.adhesiveDisclaimer")}
        </p>
        <Link to="/products?cat=tile-adhesives" className="mt-6 inline-flex items-center gap-2 text-foreground hover:text-primary" data-testid="adhesive-view-products">
          <span className="label">{t("calculators.viewTileAdhesives")}</span>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function GroutCalc() {
  const { t } = useLanguage();
  const [len, setLen] = useState("600");
  const [wid, setWid] = useState("600");
  const [thick, setThick] = useState("10");
  const [joint, setJoint] = useState("3");
  const [area, setArea] = useState("100");
  const [unit, setUnit] = useState("sqft");
  const [type, setType] = useState("1.6"); // density

  const result = useMemo(() => {
    const L = parseFloat(len) || 0;
    const W = parseFloat(wid) || 0;
    const T = parseFloat(thick) || 0;
    const J = parseFloat(joint) || 0;
    const density = parseFloat(type);
    const a = parseFloat(area) || 0;
    const areaSqm = unit === "sqft" ? a / SQFT_PER_SQM : a;
    let perSqm = 0;
    if (L > 0 && W > 0) {
      perSqm = ((L + W) / (L * W)) * J * T * density; // kg/m²
    }
    const totalKg = perSqm * areaSqm;
    return {
      perSqm: perSqm.toFixed(2),
      totalKg: totalKg.toFixed(2),
      areaSqm: areaSqm.toFixed(1),
    };
  }, [len, wid, thick, joint, area, unit, type]);

  const isEpoxy = type === "1.65";
  const bagKg = isEpoxy ? 5 : 1;
  const bags = parseFloat(result.totalKg) > 0 ? Math.ceil(parseFloat(result.totalKg) / bagKg) : 0;

  const reset = () => {
    setLen("600"); setWid("600"); setThick("10"); setJoint("3"); setArea("100"); setUnit("sqft"); setType("1.6");
  };

  return (
    <div className="grid w-full min-w-0 max-w-full overflow-hidden gap-px bg-black/10 lg:grid-cols-2" data-testid="grout-calculator">
      {/* Inputs */}
      <div className="space-y-8 bg-background p-8 md:p-12">
        <Field label={t("calculators.groutType")}>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="grout-type-select" className="h-12 rounded-none border-black/15 bg-secondary/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="1.6">{t("calculators.cementitious")}</SelectItem>
              <SelectItem value="1.65">{t("calculators.epoxy")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-6">
          <Field label={t("calculators.tileLength")}>
            <Input type="number" value={len} onChange={(e) => setLen(e.target.value)} data-testid="grout-length-input" className="h-12 rounded-none border-black/15 bg-secondary/40 font-mono text-lg" />
          </Field>
          <Field label={t("calculators.tileWidth")}>
            <Input type="number" value={wid} onChange={(e) => setWid(e.target.value)} data-testid="grout-width-input" className="h-12 rounded-none border-black/15 bg-secondary/40 font-mono text-lg" />
          </Field>
          <Field label={t("calculators.tileThickness")}>
            <Input type="number" value={thick} onChange={(e) => setThick(e.target.value)} data-testid="grout-thickness-input" className="h-12 rounded-none border-black/15 bg-secondary/40 font-mono text-lg" />
          </Field>
          <Field label={t("calculators.jointWidth")}>
            <Input type="number" value={joint} onChange={(e) => setJoint(e.target.value)} data-testid="grout-joint-input" className="h-12 rounded-none border-black/15 bg-secondary/40 font-mono text-lg" />
          </Field>
        </div>

        <Field label={t("calculators.totalTiledArea")}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input type="number" value={area} onChange={(e) => setArea(e.target.value)} data-testid="grout-area-input" className="h-12 rounded-none border-black/15 bg-secondary/40 font-mono text-lg" />
            <UnitToggle unit={unit} setUnit={setUnit} testid="grout-unit-toggle" />
          </div>
        </Field>

        <button onClick={reset} data-testid="grout-reset" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <RotateCcw className="h-4 w-4" /> <span className="label">{t("calculators.reset")}</span>
        </button>
      </div>

      {/* Results */}
      <div className="min-w-0 max-w-full overflow-hidden bg-secondary/20 p-4 sm:p-8 md:p-12">
        <span className="label text-primary">{t("calculators.estimated")}</span>
        <AnimatePresence mode="wait">
          <motion.div
            key={result.totalKg + bags}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 grid w-full min-w-0 grid-cols-1 gap-px bg-black/10 sm:grid-cols-2"
          >
            <ResultCard big value={result.totalKg} unit="kg" label={t("calculators.totalGrout")} />
            <ResultCard value={bags} unit={`× ${bagKg}kg`} label={t("calculators.packsRequired")} />
            <ResultCard value={result.perSqm} unit="kg/m²" label={t("calculators.consumption")} />
            <ResultCard value={result.areaSqm} unit="m²" label={t("calculators.area")} />
          </motion.div>
        </AnimatePresence>
        <p className="mt-6 text-xs leading-relaxed text-black/40">
          {t("calculators.groutDisclaimer")}
        </p>
        <Link to="/products?cat=grouts" className="mt-6 inline-flex items-center gap-2 text-foreground hover:text-primary" data-testid="grout-view-products">
          <span className="label">{t("calculators.viewGrouts")}</span>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

const VALID_TOOLS = ["adhesive", "grout"];

export default function Calculators() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTool = searchParams.get("tool");
  const tool = VALID_TOOLS.includes(requestedTool) ? requestedTool : "adhesive";
  const calculatorTitle = tool === "grout" ? t("calculators.groutTitle") : t("calculators.adhesiveTitle");

  const calcSeo = useMemo(() => {
    if (requestedTool === "adhesive") {
      return SEO_CONFIG.calculatorAdhesive;
    }
    if (requestedTool === "grout") {
      return SEO_CONFIG.calculatorGrout;
    }
    return SEO_CONFIG.calculators;
  }, [requestedTool]);

  useEffect(() => {
    if (requestedTool === "visualizer") {
      navigate("/grout-visualizer", { replace: true });
      return;
    }
    const toolParam = searchParams.get("tool");
    if (!VALID_TOOLS.includes(toolParam) && toolParam !== null) {
      navigate("/calculators?tool=adhesive", { replace: true });
    }
  }, [navigate, requestedTool, searchParams]);

  const calcSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": calcSeo.title,
    "description": calcSeo.description,
    "url": calcSeo.canonical,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All"
  };

  return (
    <div className="pt-20" data-testid="calculators-page">
      <SEO {...calcSeo} schema={calcSchema} />
      <section className="mx-auto max-w-[1600px] px-6 pb-12 pt-20 md:px-10 md:pb-16 md:pt-28">
        <span className="label text-primary">{t("calculators.eyebrow")}</span>
        <h1
          className={`mt-6 font-display text-4xl font-bold sm:text-6xl md:text-8xl ${
            lang === "hi"
              ? "normal-case leading-[1.15] tracking-normal"
              : "uppercase leading-[0.85] tracking-tighter"
          }`}
        >
          <MaskLine delay={0.05}>{t("calculators.h1a")}</MaskLine>
          <MaskLine delay={0.18}><span className="text-primary">{t("calculators.h1b")}</span></MaskLine>
        </h1>
        <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
          {t("calculators.intro")}
        </p>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 pb-32 md:px-10">
        <h2 className="mb-8 font-display text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
          {calculatorTitle}
        </h2>
        <AnimatePresence mode="wait">
          <motion.div
            key={tool}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            {tool === "adhesive" && <AdhesiveCalc />}
            {tool === "grout" && <GroutCalc />}
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}
